import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, ErrorCard, Icon } from '@otcmarketsgroup/otcm-ui-lib';
import { emailMarketReports } from '../../api/marketReports';
import { isEmail } from '../../utlis/validations';
import { formatDate, createArrayList } from '../../utlis/helper';
import styles from './ShareReport.module.scss';

const MAX_MESSAGE_LENGTH = 1500;

const ShareReport = ({ dialogVisible, onClose, report }) => {
    const [enablePopup, setEnablePopup] = useState(dialogVisible);
    const [recipientEmails, setRecipientEmails] = useState('');
    const [personalMessage, setPersonalMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [successShare, setSuccessShare] = useState(false);
    const [successShareMessage, setSuccessShareMessage] = useState('');
    const [emailError, setEmailError] = useState(null);
    const [shareError, setShareError] = useState(false);

    useEffect(() => {
        setEnablePopup(dialogVisible);
    }, [dialogVisible]);

    const handleShareClose = () => {
        resetState();
        onClose();
    };

    const resetState = () => {
        setEnablePopup(false);
        setRecipientEmails('');
        setPersonalMessage('');
        setSuccessShare(false);
        setSuccessShareMessage('');
        setEmailError(null);
        setShareError(false);
    };

    const handleShareSend = () => {
        resetErrors();
        if (!validateEmails()) return;

        const emailList = createArrayList(recipientEmails);
        const data = {
            emailTo: emailList,
            reportId: report?.reportId,
            ...(personalMessage && { text: personalMessage }),
        };

        setIsSending(true);

        emailMarketReports(data)
            .then(() => handleSuccess(emailList.length))
            .catch(handleError);
    };

    const resetErrors = () => {
        setSuccessShare(false);
        setSuccessShareMessage('');
        setEmailError(null);
        setShareError(false);
    };

    const validateEmails = () => {
        if (!recipientEmails || recipientEmails.trim() === '') {
            setEmailError('Please supply at least one email address.');
            return false;
        }

        const emailList = createArrayList(recipientEmails);
        if (!emailList || emailList.length === 0) {
            setEmailError('Please supply at least one email address.');
            return false;
        }

        const invalidEmailCount = emailList.filter(email => !isEmail(email)).length;
        if (invalidEmailCount > 0) {
            setEmailError(
                invalidEmailCount === 1
                    ? emailList.length === 1
                        ? 'Email address is invalid.'
                        : 'One email address is invalid.'
                    : 'Multiple email addresses are invalid.'
            );
            return false;
        }

        return true;
    };

    const handleSuccess = (emailCount) => {
        setSuccessShare(true);
        setSuccessShareMessage(
            emailCount === 1
                ? 'Market Report was sent to the email address provided.'
                : 'Market Report was sent to the list of email addresses provided.'
        );
        setIsSending(false);
    };

    const handleError = (error) => {
        console.error(error?.response?.data?.error || 'Error sharing Market Report.');
        setShareError('Error sharing Market Report.');
        setIsSending(false);
    };

    return <>
        {!successShare && <Dialog open={dialogVisible} onClose={handleShareClose}>
            <DialogTitle
                onClose={handleShareClose}
                title={`OTC Market Report ${report?.symbol} - ` + formatDate(report?.date)}
            />
            <DialogContent>
                <div className={styles.textField}>
                    <TextField
                        helperText='Separate multiple addresses with a comma.'
                        label='Recipient Email(s)'
                        onChange={(e) => setRecipientEmails(e.target.value)}
                        placeholder='email@email.com'
                        type='text'
                        value={recipientEmails}
                    />
                </div>
                <div className={styles.textField}>
                    <TextField
                        label='Add a Personal Message'
                        maxLength={MAX_MESSAGE_LENGTH}
                        multiline
                        onChange={(e) => setPersonalMessage(e.target.value)}
                        placeholder='Enter Personal Message'
                        showCharCounter
                        type='text'
                        value={personalMessage}
                    />
                </div>
                {(emailError || shareError) && <Box mt={2}>
                    <ErrorCard title={emailError || shareError}></ErrorCard>
                </Box>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleShareClose} isLoading={isSending} variant='outlined'>
                    Cancel
                </Button>
                <Button onClick={handleShareSend} isLoading={isSending} variant='contained'>
                    Send
                </Button>
            </DialogActions>
        </Dialog>}

        {successShare && <Dialog open={dialogVisible} onClose={handleShareClose}>
            <DialogTitle
                onClose={handleShareClose}
                title={`OTC Market Report ${report?.symbol} - ` + formatDate(report?.date)}
            />
            <DialogContent>
                <div className={styles.shareSuccessful}>
                    <Icon icon='check-circle' size={30} />
                    <Typography variant='subtitle1'>
                        {successShareMessage}
                    </Typography>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleShareClose} variant='contained'>
                    OK
                </Button>
            </DialogActions>
        </Dialog>}
    </>;
};

ShareReport.propTypes = {
    dialogVisible: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    report: PropTypes.shape({
        symbol: PropTypes.string.isRequired,
        date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
        reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
};

export default ShareReport;
